import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Modal } from "../../components/modal";
import { UserForm, UserFormValues } from "../../components/user-form";
import { useGetAssociationMembers } from "../../queries/association-member";
import { useGetAuthContext } from "../../queries/get-auth-context";
import { updateAssociationUser } from "../../services/association-user";
import { navigateBack } from "../../utils/navigate-back";
import { recordIdToId } from "../../utils/record-id-to-id";

const emptyFormValues: UserFormValues = {
  email: "",
  firstName: "",
  lastName: "",
  sendInvite: false,
  isOwner: false,
  electedRepresentativeType: "",
  password: "",
};

export function UserEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("User ID is required");
  }

  const { data: members, isLoading } = useGetAssociationMembers();
  const member = members?.find((m) => recordIdToId(m.id) === id);
  const { data: authContext } = useGetAuthContext();
  const isOwner =
    authContext?.type === "associationUser" && authContext.isOwner;
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [formValues, setFormValues] = useState<UserFormValues>(emptyFormValues);

  useEffect(() => {
    if (member) {
      setFormValues({
        email: member.email,
        firstName: member.firstName ?? "",
        lastName: member.lastName ?? "",
        sendInvite: false,
        isOwner: member.isOwner,
        electedRepresentativeType: member.electedRepresentativeType ?? "",
        password: "",
      });
    }
  }, [member]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/user/list");
  };

  if (authContext && !isOwner) {
    return <Navigate replace to="/admin/user/list" />;
  }

  return (
    <Modal open onClose={closeModal}>
      <UserForm
        hasError={hasError}
        loading={isLoading || loading}
        mode="edit"
        values={formValues}
        onCancel={closeModal}
        onChange={setFormValues}
        onSubmit={() => {
          void (async () => {
            setLoading(true);
            setHasError(false);
            try {
              await updateAssociationUser(id, {
                email: formValues.email,
                firstName: formValues.firstName.trim(),
                lastName: formValues.lastName.trim(),
                electedRepresentativeType:
                  formValues.electedRepresentativeType || null,
                ...(formValues.password ? { password: formValues.password } : {}),
              });
              await queryClient.invalidateQueries({
                queryKey: ["associationMembers"],
              });
              closeModal();
            } catch {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
